import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Download, Loader2, Mail, RefreshCw, RotateCcw, Search, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { EmailReportButton } from "../components/EmailReportButton";
import VerifEditor from "../components/VerifEditor";
import PepPanamaList, { PepPanamaView, isCargoAlert } from "../components/PepPanamaList";
import { useAuth } from "../context/AuthContext";

function ResultBanner({ r, t, onNew }) {
  const match = r.result === "match";
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 border ${match ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="pep-result-banner">
      {match ? <ShieldAlert className="w-6 h-6 text-red-600" /> : <ShieldCheck className="w-6 h-6 text-emerald-600" />}
      <div className="flex-1 min-w-[180px]">
        <div className={`font-heading font-extrabold ${match ? "text-red-700" : "text-emerald-700"}`} data-testid="pep-result-text">
          {match ? t("p.found") : t("search.no_match")}
        </div>
        <div className="text-xs text-slate-600">
          {t("search.consult_no")} <span className="font-mono font-semibold" data-testid="pep-consult-number">{r.consult_number}</span>
          {match && ` · ${r.match_count} ${t("search.matches_found")}`}
        </div>
      </div>
      <button
        onClick={onNew}
        data-testid="pep-new-search-button"
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-700 hover:border-slate-500 transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" /> {t("search.new")}
      </button>
      <EmailReportButton pdfPath={`/pep/searches/${r.id}/pdf`} filename={`consulta_${r.consult_number}.pdf`} subject={`Reporte de consulta PEP/RCA ${r.consult_number}`} module="PEP y RCA" summary={`${r.query} — ${r.match_count} perfil(es)`} testid="pep-email-report" />
      <button
        onClick={() => downloadFile(`/pep/searches/${r.id}/pdf`, `consulta_${r.consult_number}.pdf`)}
        data-testid="pep-download-pdf"
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C] transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
      </button>
    </div>
  );
}

function MatchesTable({ matches, t }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="pep-matches-table">
      <table className="w-full text-sm min-w-[860px]">
        <thead>
          <tr className="bg-[#16335F] text-white text-left">
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("p.col_category")}</th>
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_name")}</th>
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("p.col_position")}</th>
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("p.col_country")}</th>
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("p.col_since")}</th>
            <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("search.col_score")}</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m) => (
            <tr key={m.os_id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2.5">
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${m.source === "PEP_PANAMA" ? "bg-blue-100 text-blue-800" : m.category === "PEP" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{m.source === "PEP_PANAMA" ? "PEP-PA" : m.category}</span>
                {m.source === "PEP_PANAMA" && <div className="text-[10px] font-bold text-blue-700 mt-0.5">{t("pp.badge")}{m.pep_pa?.state_organ ? ` · ${m.pep_pa.state_organ}` : ""}{m.pep_pa?.source_label ? ` · ${m.pep_pa.source_label}` : ""}</div>}
              </td>
              <td className="px-3 py-2.5 font-semibold text-slate-800">
                {m.name}
                {m.matched_alias && <div className="text-xs font-normal text-amber-700">{t("search.alias")}: {m.matched_alias}</div>}
                {m.identifiers?.length > 0 && <div className="text-xs font-normal text-slate-500">ID: {m.identifiers.slice(0, 2).join("; ")}</div>}
              </td>
              <td className={`px-3 py-2.5 text-xs ${isCargoAlert(m.position) ? "text-red-600 font-bold" : "text-slate-600"}`}>{m.position || "—"}</td>
              <td className="px-3 py-2.5 text-slate-600 text-xs">{(m.countries || []).join(", ") || "—"}</td>
              <td className="px-3 py-2.5 text-slate-500 text-xs font-mono">{m.first_seen || "—"}</td>
              <td className="px-3 py-2.5 text-right">
                <span className={`font-mono font-bold ${m.score >= 90 ? "text-red-600" : m.score >= 80 ? "text-amber-600" : "text-slate-600"}`}>{m.score}%</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PepPanamaPage() {
  const { t } = useLang();
  return (
    <div className="space-y-6" data-testid="pep-panama-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("pp.title")}</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t("pp.readonly_note")}</p>
      </div>
      <PepPanamaView />
    </div>
  );
}

export default function PepSearch() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("individual");
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const newSearch = () => { setResult(null); setQuery(""); setBulkResults([]); setBulkText(""); };

  const loadStatus = () => api.get("/pep/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { if (user?.role !== "superadmin") loadStatus(); }, [user]);

  const refresh = async () => {
    try {
      await api.post("/pep/refresh");
      toast.success(t("search.refresh_started"));
      setTimeout(loadStatus, 8000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/pep/search", { query: query.trim(), category, entity_type: entityType });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const runBulk = async () => {
    const queries = bulkText.split("\n").map((s) => s.trim()).filter((s) => s.length >= 2);
    if (queries.length === 0) return;
    setBulkBusy(true);
    setBulkResults([]);
    try {
      const { data } = await api.post("/pep/search/bulk", { queries, category, entity_type: entityType });
      setBulkResults(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  if (user?.role === "superadmin") {
    return (
      <div className="space-y-6" data-testid="pep-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("pp.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t("pp.subtitle")}</p>
        </div>
        <PepPanamaList />
      </div>
    );
  }

  const tmods = user?.tenant_modules ?? null;
  const isClientPepPaOnly = user?.role === "client" && !(user?.modules || []).includes("pep") && (user?.modules || []).includes("pep_panama");

  if (isClientPepPaOnly) {
    return <Navigate to="/lista-pep-panama" replace />;
  }

  const statusCards = [
    { key: "OS_PEP", label: "PEP", desc: "p.status_pep" },
    { key: "OS_CRIME", label: "RCA", desc: "p.status_rca" },
  ];

  return (
    <div className="space-y-6" data-testid="pep-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("p.title")}</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t("p.subtitle")}</p>
        </div>
        {user?.role === "admin" && (
          <button onClick={refresh} data-testid="pep-refresh-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:border-slate-500 transition-colors">
            <RefreshCw className="w-4 h-4" /> {t("search.refresh")}
          </button>
        )}
      </div>

      {status && (
        <div className="grid sm:grid-cols-2 gap-4">
          {statusCards.map((c) => {
            const s = status[c.key] || {};
            return (
              <div key={c.key} className="ec-card px-5 py-4 flex items-center justify-between" data-testid={`pep-status-${c.key}`}>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-400">OpenSanctions — {c.label}</div>
                  <div className="font-heading text-xl font-extrabold text-slate-900">{(s.count || 0).toLocaleString()} <span className="text-sm font-semibold text-slate-500">{t("search.entries")}</span></div>
                  <div className="text-xs text-slate-500">{t("search.last_update")}: {s.last_update ? fmtDate(s.last_update) : "—"}</div>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.status === "ok" ? "bg-emerald-50 text-emerald-700" : s.status === "error" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                  {s.status === "ok" ? t("search.status_ok") : s.status === "error" ? t("search.status_error") : t("search.status_pending")}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-slate-400">{t("p.attribution")}</p>

      <div className="flex rounded-lg bg-slate-200/70 p-1 w-fit">
        <button data-testid="pep-tab-individual" onClick={() => setTab("individual")} className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${tab === "individual" ? "bg-[#16335F] text-white" : "text-slate-600"}`}>
          {t("search.individual")}
        </button>
        <button data-testid="pep-tab-bulk" onClick={() => setTab("bulk")} className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${tab === "bulk" ? "bg-[#16335F] text-white" : "text-slate-600"}`}>
          {t("search.bulk")}
        </button>
      </div>

      {tab === "individual" ? (
        <div className="ec-card p-6 space-y-5" data-testid="pep-individual-panel">
          <form onSubmit={runSearch} className="space-y-3">
            <div className="grid md:grid-cols-[1fr_200px_220px_auto] gap-5 items-end">
              <div>
                <label className="ec-label">{t("p.query_label")}</label>
                <input className="ec-input !text-lg !py-3.5 font-medium" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("p.query_ph")} data-testid="pep-search-input" />
              </div>
              <div>
                <label className="ec-label">{t("p.category")}</label>
                <select className="ec-input !text-base !py-3.5 font-medium" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="pep-category-select">
                  <option value="all">{t("p.cat_all")}</option>
                  <option value="PEP">PEP</option>
                  <option value="RCA">RCA</option>
                </select>
              </div>
              <div>
                <label className="ec-label">{t("search.type_label")}</label>
                <select className="ec-input !text-base !py-3.5 font-medium" value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="pep-entity-type-select">
                  <option value="all">{t("search.t_all")}</option>
                  <option value="person">{t("search.t_person")}</option>
                  <option value="entity">{t("search.t_entity")}</option>
                </select>
              </div>
              <button type="submit" disabled={busy} data-testid="pep-search-submit" className="flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-[#044CBC] text-white text-base font-extrabold shadow-lg shadow-orange-300/50 hover:bg-[#033A8C] hover:shadow-xl hover:shadow-orange-300/60 hover:-translate-y-0.5 transition-all disabled:opacity-60 whitespace-nowrap">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                {busy ? t("search.running") : t("search.run")}
              </button>
            </div>
            <p className="text-[11px] text-slate-400">{t("p.id_note")}</p>
          </form>
          {result && (
            <div className="space-y-4">
              <ResultBanner r={result} t={t} onNew={newSearch} />
              <VerifEditor endpoint={`/pep/searches/${result.id}`} doc={result} testid="pv" onSaved={(v) => setResult({ ...result, ...v })} />
              {result.matches?.length > 0 && <MatchesTable matches={result.matches} t={t} />}
            </div>
          )}
        </div>
      ) : (
        <div className="ec-card p-6 space-y-5" data-testid="pep-bulk-panel">
          <div>
            <label className="ec-label">{t("search.bulk")}</label>
            <p className="text-xs text-slate-500 mb-2">{t("search.bulk_help")}</p>
            <textarea className="ec-input font-mono" rows={7} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"Vladimir Putin\n8-888-888\nEmpresa S.A."} data-testid="pep-bulk-input" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <select className="ec-input w-auto" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="pep-bulk-category-select">
              <option value="all">{t("p.cat_all")}</option>
              <option value="PEP">PEP</option>
              <option value="RCA">RCA</option>
            </select>
            <select className="ec-input w-auto" value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="pep-bulk-entity-select">
              <option value="all">{t("search.t_all")}</option>
              <option value="person">{t("search.t_person")}</option>
              <option value="entity">{t("search.t_entity")}</option>
            </select>
            <button onClick={runBulk} disabled={bulkBusy} data-testid="pep-bulk-submit" className="flex items-center gap-2 px-8 py-3.5 rounded-lg bg-[#044CBC] text-white text-base font-extrabold shadow-lg shadow-orange-300/50 hover:bg-[#033A8C] hover:shadow-xl hover:shadow-orange-300/60 hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {bulkBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {bulkBusy ? t("search.running") : t("search.bulk_run")}
            </button>
          </div>
          {bulkResults.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="pep-bulk-results">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-[#16335F] text-white text-left">
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.col_query")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("search.consult_no")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{t("common.result")}</th>
                    <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkResults.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pep-bulk-result-${r.consult_number}`}>
                      <td className="px-3 py-2.5 font-semibold text-slate-800">{r.query}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{r.consult_number}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.result === "match" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {r.result === "match" ? `${t("p.found")} (${r.match_count})` : t("search.no_match")}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => downloadFile(`/pep/searches/${r.id}/pdf`, `consulta_${r.consult_number}.pdf`)} data-testid={`pep-bulk-pdf-${r.consult_number}`} className="text-[#044CBC] hover:text-[#033A8C] font-semibold text-xs inline-flex items-center gap-1">
                          <Download className="w-3.5 h-3.5" /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
